import { FormDeleteAnswersDocument, FormsAnswerFragment } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type deleteAnswersProps = {
  /**
   * The form membership Id to delete answers from
   */
  membershipId: string

  /**
   * Success callback that will be called if answers are removed correctly
   * @param answers
   */
  onSuccess?: (answers: Array<FormsAnswerFragment> | undefined | null) => void

  /**
   * Error callback that will be called if an error ocurred
   * @param errors
   */
  onError?: (errors: Record<string, string>) => void
}

export const useDeleteAnswers = () => {
  const [mutate, { loading }] = useMutation(FormDeleteAnswersDocument, {
    extractorFn: (data) => data.formsMutations.bulkDeleteMembershipAnswersFormsAnswer,
    update: (cache, { data }) => {
      const deletedAnswers =
        data?.formsMutations.bulkDeleteMembershipAnswersFormsAnswer.answers || []
      // Remove all answers from cache
      deletedAnswers.forEach((answer) => {
        cache.evict({ id: cache.identify(answer) })
      })

      const membershipId = deletedAnswers ? deletedAnswers[0]?.membership?.id : undefined
      if (membershipId) {
        cache.modify({
          id: `FormsMembership:${membershipId}`,
          fields: {
            answersConnection(existingAnswerConnection) {
              const existingTotalCount = existingAnswerConnection.totalCount || 0
              return {
                ...existingAnswerConnection,
                totalCount: existingTotalCount + 1,
              }
            },
            answers(_existingAnswers) {
              return []
            },
            score(_existingScore) {
              return 0
            },
          },
        })
      }

      cache.gc()
    },
  })

  return {
    deletingAnswers: loading,
    deleteAnswers: async ({ membershipId, onSuccess, onError }: deleteAnswersProps) => {
      const result = await mutate({
        variables: { membershipId },
      })

      const handled = handleResult(result, (errors) => {
        // call error callback if any or show a generic error
        if (onError) {
          onError(errors)
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('common.error'),
          })
        }
      })

      //call success callback if any
      if (handled.success && onSuccess) {
        onSuccess(handled.data.answers)
      }

      return handled.success
    },
  }
}
